import React, { FC, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useMutation } from '@apollo/client';
import { Flex, Heading } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import ErrorCard from 'components/ErrorCard';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import TrackView from 'features/Analytics/TrackView';
import NavigationHeader from 'features/Payments/Pages/Transfers/components/NavigationHeader';
import * as ContentLayout from 'layouts/ContentLayout';

import {
  BankTransferCreationStep,
  useBankTransferContext,
} from '../../../../contexts';
import { InternationalTransferRecipientList } from './components/InternationalTransferRecipientList';
import { NewInternationalTransferRecipient } from './components/NewInternationalTransferRecipient';
import { SelectInternationalTransferRecipientDocument } from './graphql/selectInternationalTransferRecipient.gql';
import locales from './locales';
import { Recipient } from './types';

export const InternationalTransferRecipient: FC<
  StepProps<BankTransferCreationStep>
> = ({ goBack, goNext }) => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferRecipient',
    );
  }
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferRecipient',
    );
  }

  const { showToast } = useToast();
  const [variant, setVariant] = useState<'list' | 'new' | 'edit'>('list');
  const {
    bankTransfer: {
      quote: { internationalTransferQuoteId },
    },
    setBankTransfer,
  } = useBankTransferContext('internationalTransferRecipient');
  const [selectRecipient] = useMutation(
    SelectInternationalTransferRecipientDocument,
  );

  const handleCreateRecipientClick = () => {
    setVariant('new');
  };

  const handleNewRecipientBackClick = () => {
    setVariant('list');
  };

  const handleRecipientSelected = ({
    internationalTransferRecipientId,
    ...recipient
  }: Recipient) => {
    setBankTransfer((prev) => ({
      ...prev,
      recipient: {
        id: internationalTransferRecipientId,
        ...recipient,
      },
    }));
    goNext();
  };

  const handleRecipientClick = ({
    internationalTransferRecipientId,
    ...recipient
  }: Recipient) => {
    selectRecipient({
      onCompleted: (data) => {
        if (data.selectInternationalTransferRecipient.requireUpdate) {
          setVariant('edit');
        } else {
          handleRecipientSelected({
            internationalTransferRecipientId,
            ...recipient,
          });
        }
      },
      onError: () => {
        showToast({ message: locales.failedToSelectRecipient, type: 'error' });
      },
      variables: {
        input: {
          companyProfileId,
          companyUserId,
          internationalTransferQuoteId,
          internationalTransferRecipientId,
          lang: locales.getLanguage(),
        },
      },
    });
  };

  if (variant === 'new' || variant === 'edit') {
    return (
      <NewInternationalTransferRecipient
        onBackClick={handleNewRecipientBackClick}
        onRecipientCreated={handleRecipientSelected}
        variant={variant}
      />
    );
  }

  return (
    <ContentLayout.Root>
      <TrackView pageName="InternationalTransferRecipient" />
      <ContentLayout.Body>
        <NavigationHeader onBack={goBack} />
        <Flex align="center" justify="space-between">
          <Heading size="lg">{locales.recipient}</Heading>
          <Button
            icon="user-plus"
            iconPosition="left"
            onClick={handleCreateRecipientClick}
          >
            {locales.newRecipient}
          </Button>
        </Flex>
        <ContentLayout.BodyScrollableSection paddingTop={40}>
          <ErrorBoundary
            fallbackRender={({ resetErrorBoundary }) => (
              <ErrorCard retry={resetErrorBoundary} />
            )}
          >
            <InternationalTransferRecipientList
              onRecipientClick={handleRecipientClick}
              onRecipientCreateClick={handleCreateRecipientClick}
            />
          </ErrorBoundary>
        </ContentLayout.BodyScrollableSection>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};
